import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Box,
  Typography,
  Avatar,
  alpha,
  useTheme,
  Skeleton,
} from "@mui/material";

const ListTable = ({
  getCandidateId,
  data,
  handlePageChange,
  currentPage,
  totalRecords,
  activeId,
  isLoading = false,
}) => {
  const theme = useTheme();
  const [selectedCandidateId, setSelectedCandidateId] = useState(activeId || null);

  const handleRowClick = (id) => {
    setSelectedCandidateId(id);
    if (getCandidateId) {
      getCandidateId(id);
    }
  };

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return "?";
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const rowsPerPage = 15;
  const totalPages = Math.ceil((totalRecords || 0) / rowsPerPage);

  if (isLoading) {
    return (
      <Box sx={{ height: "75vh", overflowY: "auto" }}>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                <TableCell
                  sx={{
                    color: "#344054",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    py: 1,
                    px: 1.5,
                    borderBottom: "1px solid #EAECF0",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  #
                </TableCell>
                <TableCell
                  sx={{
                    color: "#344054",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    py: 1,
                    px: 1.5,
                    borderBottom: "1px solid #EAECF0",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Candidate
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(8)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ py: 1, px: 1.5 }}>
                    <Skeleton variant="circular" width={20} height={20} />
                  </TableCell>
                  <TableCell sx={{ py: 1, px: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Skeleton variant="circular" width={32} height={32} />
                      <Skeleton variant="text" width={120} height={20} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "75vh", overflowY: "auto" }}>
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  bgcolor: "#F8FAFC",
                  color: "#344054",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  py: 1,
                  px: 1.5,
                  width: 40,
                  borderBottom: "1px solid #EAECF0",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                #
              </TableCell>
              <TableCell
                sx={{
                  bgcolor: "#F8FAFC",
                  color: "#344054",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  py: 1,
                  px: 1.5,
                  borderBottom: "1px solid #EAECF0",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Candidate
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.length > 0 ? (
              data.map((candidate, index) => {
                const isSelected = selectedCandidateId === candidate.id;
                const initials = getInitials(candidate.first_name, candidate.last_name);

                return (
                  <TableRow
                    key={candidate.id}
                    onClick={() => handleRowClick(candidate.id)}
                    sx={{
                      bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : "transparent",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        bgcolor: isSelected
                          ? alpha(theme.palette.primary.main, 0.08)
                          : alpha(theme.palette.primary.main, 0.02),
                      },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={{ py: 1, px: 1.5, borderBottom: "1px solid #F2F4F7" }}>
                      {isSelected ? (
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            bgcolor: theme.palette.primary.main,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "0.6rem",
                            fontWeight: 700,
                          }}
                        >
                          ✓
                        </Box>
                      ) : (
                        <Typography
                          variant="caption"
                          color="#94A3B8"
                          sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                        >
                          {index + 1}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1.5, borderBottom: "1px solid #F2F4F7" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          src={candidate.profile_photo}
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: isSelected
                              ? theme.palette.primary.main
                              : alpha(theme.palette.primary.main, 0.08),
                            color: isSelected ? "white" : theme.palette.primary.main,
                            fontSize: "0.65rem",
                            fontWeight: 600,
                          }}
                        >
                          {!candidate.profile_photo && initials}
                        </Avatar>
                        <Typography
                          variant="body2"
                          fontWeight={isSelected ? 600 : 500}
                          color="#101828"
                          sx={{ fontSize: "0.85rem" }}
                        >
                          {candidate.first_name} {candidate.last_name}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="#94A3B8">
                    No candidates found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 1.5,
            borderTop: "1px solid #EAECF0",
            bgcolor: "#F8FAFC",
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage || 1}
            onChange={(event, page) => handlePageChange(page)}
            size="small"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                fontSize: "0.75rem",
              },
              "& .Mui-selected": {
                bgcolor: "#2469F0",
                color: "white",
                "&:hover": {
                  bgcolor: "#1A5BD4",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ListTable;